declare const Buffer: any;

const AUTH_STORAGE_KEY = 'dtUsuario_secure';
const SECRET_KEY = (process.env.REACT_APP_AUTH_SECRET ?? 'halconet_auth_secret').toString();

const textEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : undefined;
const textDecoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : undefined;

const hasWindowContext = typeof window !== 'undefined';

const getSecretKeyBytes = () => {
    const normalizedKey = SECRET_KEY.length > 0 ? SECRET_KEY : 'halconet_auth_secret';

    if (textEncoder) {
        return textEncoder.encode(normalizedKey);
    }

    if (typeof Buffer !== 'undefined') {
        return new Uint8Array(Buffer.from(normalizedKey));
    }

    throw new Error('TextEncoder is not available in this environment.');
};

const base64Encode = (bytes: Uint8Array) => {
    if (hasWindowContext && typeof window.btoa === 'function') {
        let binary = '';
        bytes.forEach(byte => {
            binary += String.fromCharCode(byte);
        });
        return window.btoa(binary);
    }

    if (typeof Buffer !== 'undefined') {
        return Buffer.from(bytes).toString('base64');
    }

    throw new Error('No base64 encoder available.');
};

const base64Decode = (value: string) => {
    if (hasWindowContext && typeof window.atob === 'function') {
        const binary = window.atob(value);
        const output = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            output[i] = binary.charCodeAt(i);
        }
        return output;
    }

    if (typeof Buffer !== 'undefined') {
        const buffer = Buffer.from(value, 'base64');
        return new Uint8Array(buffer);
    }

    throw new Error('No base64 decoder available.');
};

const xorTransform = (input: Uint8Array, key: Uint8Array) => {
    const output = new Uint8Array(input.length);
    for (let i = 0; i < input.length; i++) {
        output[i] = input[i] ^ key[i % key.length];
    }
    return output;
};

const encryptPayload = (value: string) => {
    if (!textEncoder) {
        return value;
    }
    const dataBytes = textEncoder.encode(value);
    const keyBytes = getSecretKeyBytes();
    const encrypted = xorTransform(dataBytes, keyBytes);
    return base64Encode(encrypted);
};

const decryptPayload = (value: string) => {
    if (!textDecoder) {
        return value;
    }

    try {
        const encryptedBytes = base64Decode(value);
        const keyBytes = getSecretKeyBytes();
        const decrypted = xorTransform(encryptedBytes, keyBytes);
        return textDecoder.decode(decrypted);
    } catch {
        return value;
    }
};

let cachedAuthData: any | null = null;

const sanitizeAuthPayload = (rawData: any) => {
    if (!rawData || typeof rawData !== 'object') {
        return null;
    }

    const { password, ...rest } = rawData;
    return rest;
};

export const storeAuthData = (rawData: any) => {
    const sanitizedPayload = sanitizeAuthPayload(rawData);
    if (!sanitizedPayload) {
        return null;
    }

    cachedAuthData = sanitizedPayload;

    if (!hasWindowContext) {
        return sanitizedPayload;
    }

    try {
        const encrypted = encryptPayload(JSON.stringify(sanitizedPayload));
        window.localStorage.setItem(AUTH_STORAGE_KEY, encrypted);
    } catch (error) {
        console.error('No fue posible proteger los datos de autenticación.', error);
    }

    return sanitizedPayload;
};

export const clearAuthData = () => {
    cachedAuthData = null;

    if (!hasWindowContext) {
        return;
    }

    try {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (error) {
        console.error('No fue posible eliminar los datos de autenticación almacenados.', error);
    }
};

export const GetAPI = () => {
    return `https://seahorse-app-g4bew.ondigitalocean.app`
    // return `http://localhost:3001`
}

export const getCookie = () => {
    if (cachedAuthData) {
        return cachedAuthData;
    }

    if (!hasWindowContext) {
        return null;
    }

    const encryptedPayload = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!encryptedPayload) {
        return null;
    }

    try {
        const decrypted = decryptPayload(encryptedPayload);
        const parsed = JSON.parse(decrypted);
        cachedAuthData = parsed;
        return parsed;
    } catch (error) {
        console.error('No fue posible leer los datos de autenticación almacenados.', error);
        clearAuthData();
        return null;
    }
}

export const NullUndefinedEmpty = (variable: any) => {
    let val: boolean = true;
    if (variable === null) val = true;
    else if (variable === undefined) val = true;
    else if (variable === "") val = true;
    else val = false;
    return val;
}

export const GetCategories = async () => {
    const data = getCookie();
    const api = GetAPI()
    const dtresult = await fetch(`${api}/catalogs/categories`, { method: 'GET', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.token}` } });
    let categorias = await dtresult.json();
    return categorias.data;
}

export const GenerarUUID = () => {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
        ((+c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> (+c / 4)).toString(16)
    );
};

export const OpenFile = (url:string) => {
    let link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
}

export const postData = async (fnctn: string = "", method: string = "", objt: { [key: string]: any } = {}) => {
    const api = GetAPI();
    const data = getCookie();

    const options: RequestInit = {
        method,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.token}`
        }
    };

    const methodAllowsBody = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
    if (methodAllowsBody && objt) options.body = JSON.stringify(objt);

    return await fetch(`${api}${fnctn}`, options);
};